import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { gql, useQuery, useMutation } from '@apollo/client';
import { useParams, useHistory, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { hasPermissionAixcDeploy } from 'shared/lib/hasPermission';
// DISABLED: Socket imports removed - lock logic disabled, to be revisited
// import { io, Socket } from 'socket.io-client';
import Button from '~/app/components/Button';
import UIButton from '~/app/ui/Button';
import ConfirmDialog from '~/app/ui/ConfirmDialog';
import { S, A, TONES, DARK } from '~/app/ui/tokens';
// import { getAppURL } from 'shared/lib/clientSettings';
// import { WEBSOCKET_MSG_ID } from 'shared/constants';
import CodeEditor from './CodeEditor';
import LivePreview from './LivePreview';
import PropsSchemaEditor from './PropsSchemaEditor';
import AiChatPanel from './AiChatPanel';
import PublishImpact from './PublishImpact';
import RedeployModal from './RedeployModal';
import PasteCodeModal from './PasteCodeModal';
import { parsePropsJson, exportPropsJson } from '~/lib/propsJsonImport';
import { enrichPropsSchemaItems } from 'shared/socialIconEnumOptions';

const GET_CUSTOM_WIDGET = gql`
  query GetCustomWidget($id: ID!) {
    customWidget(id: $id) {
      _id
      name
      title
      description
      category
      publishedVersion
      isActive
      widgetId
      versions {
        versionId
        html
        css
        js
        propsSchema {
          key
          label
          type
          defaultValue
          options {
            label
            value
          }
          children {
            key
            label
            type
            defaultValue
            options {
              label
              value
            }
            children {
              key
              label
              type
              defaultValue
              options {
                label
                value
              }
            }
          }
        }
        defaultProps
        changelog
        createdAt
        scoper
      }
      lockedBy {
        userId
        userName
        lockedAt
        socketId
      }
    }
  }
`;

const SAVE_WIDGET_VERSION = gql`
  mutation SaveCustomWidgetVersion($id: ID!, $version: CustomWidgetVersionInput!) {
    saveCustomWidgetVersion(id: $id, version: $version) {
      _id
      versions {
        versionId
        html
        css
        js
        propsSchema {
          key
          label
          type
          defaultValue
          options {
            label
            value
          }
          children {
            key
            label
            type
            defaultValue
            options {
              label
              value
            }
            children {
              key
              label
              type
              defaultValue
              options {
                label
                value
              }
            }
          }
        }
        defaultProps
        changelog
        createdAt
        scoper
      }
    }
  }
`;

const PUBLISH_WIDGET_VERSION = gql`
  mutation PublishCustomWidgetVersion($id: ID!, $versionId: String!) {
    publishCustomWidgetVersion(id: $id, versionId: $versionId) {
      _id
      publishedVersion
    }
  }
`;

// DISABLED: Lock mutations - lock logic disabled, to be revisited
// const LOCK_WIDGET = gql`
//   mutation LockCustomWidget($id: ID!, $socketId: String!) {
//     lockCustomWidget(id: $id, socketId: $socketId) {
//       success
//       message
//       lockedBy {
//         userId
//         userName
//         lockedAt
//         socketId
//       }
//     }
//   }
// `;

// const UNLOCK_WIDGET = gql`
//   mutation UnlockCustomWidget($id: ID!) {
//     unlockCustomWidget(id: $id) {
//       success
//       message
//     }
//   }
// `;

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    background: '#f8fafc',
    [DARK]: { background: S.chrome },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    background: A.surface,
    borderBottom: '1px solid #e2e8f0',
    flexWrap: 'wrap',
    gap: '12px',
    [DARK]: { borderBottomColor: S.line },
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backLink: {
    color: '#64748b',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    '&:hover': {
      color: '#0f172a',
    },
    [DARK]: { color: S.muted, '&:hover': { color: S.fg } },
  },
  titleSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    margin: 0,
    color: '#0f172a',
    [DARK]: { color: S.fg },
  },
  widgetId: {
    fontSize: '12px',
    color: '#64748b',
    fontFamily: 'monospace',
    [DARK]: { color: S.muted },
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  lockWarning: {
    background: '#fef3c7',
    color: '#92400e',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    [DARK]: { background: TONES.amber.bg, color: TONES.amber.fg },
  },
  versionWarningBanner: {
    background: '#fef3c7',
    color: '#92400e',
    padding: '12px 24px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #fcd34d',
    [DARK]: { background: TONES.amber.bg, color: TONES.amber.fg, borderBottomColor: S.line },
  },
  versionWarningText: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  versionActions: {
    display: 'flex',
    gap: 8,
    flexShrink: 0,
  },
  versionSelect: {
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    background: S.field,
    cursor: 'pointer',
    [DARK]: { color: S.fg, borderColor: S.fieldLine },
  },
  body: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  // Sidebar styles with collapse support
  sidebar: {
    width: '300px',
    minWidth: '200px',
    maxWidth: '500px',
    background: A.surface,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    flexShrink: 0,
  },
  sidebarCollapsed: {
    width: '0px !important',
    minWidth: '0px !important',
    '& > *': {
      opacity: 0,
      visibility: 'hidden',
    },
  },
  // Sidebar resize divider
  sidebarResizeDivider: {
    width: '8px',
    background: '#e2e8f0',
    cursor: 'col-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease',
    position: 'relative',
    flexShrink: 0,
    '&:hover': {
      background: '#3b82f6',
    },
    '&::before': {
      content: '""',
      width: '4px',
      height: '60px',
      background: '#94a3b8',
      borderRadius: '2px',
    },
    '&:hover::before': {
      background: '#ffffff',
    },
    [DARK]: {
      background: S.line,
      '&:hover': { background: S.blue },
      '&::before': { background: S.muted },
      '&:hover::before': { background: S.onFg },
    },
  },
  sidebarResizeDividerActive: {
    background: '#3b82f6',
    '&::before': {
      background: '#ffffff',
    },
    [DARK]: { background: S.blue, '&::before': { background: S.onFg } },
  },
  sidebarSection: {
    padding: '16px',
    borderBottom: '1px solid #e2e8f0',
    [DARK]: { borderBottomColor: S.line },
  },
  sidebarTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '12px',
    [DARK]: { color: S.muted },
  },
  // Settings tab text (was inline)
  sidebarText: {
    color: '#64748b',
    [DARK]: { color: S.muted },
  },
  sidebarNote: {
    color: '#94a3b8',
    [DARK]: { color: S.muted },
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #e2e8f0',
    background: A.surface,
    [DARK]: { borderBottomColor: S.line },
  },
  tab: {
    flex: 1,
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 500,
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    color: '#64748b',
    transition: 'all 0.2s ease',
    '&:hover': {
      color: '#0f172a',
      background: '#f8fafc',
    },
    [DARK]: { color: S.muted, '&:hover': { color: S.fg, background: A.rowHover } },
  },
  tabActive: {
    color: '#0f172a',
    borderBottomColor: '#3b82f6',
    [DARK]: { color: S.fg, borderBottomColor: S.blue },
  },
  // Main content with resizable support
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
    minWidth: '200px',
    transition: 'flex 0.3s ease, width 0.3s ease',
  },
  mainContentCollapsed: {
    flex: '0 0 0px !important',
    width: '0px !important',
    minWidth: '0px',
    overflow: 'hidden',
    '& > *': {
      opacity: 0,
      visibility: 'hidden',
    },
  },
  editorContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  },
  editorTabs: {
    display: 'flex',
    background: '#1e293b',
    padding: '8px 16px 0',
    gap: '4px',
    alignItems: 'center',
  },
  editorTab: {
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 500,
    background: 'transparent',
    border: 'none',
    borderRadius: '6px 6px 0 0',
    cursor: 'pointer',
    color: '#94a3b8',
    transition: 'all 0.2s ease',
    '&:hover': {
      color: '#e2e8f0',
      background: 'rgba(255, 255, 255, 0.05)',
    },
  },
  editorTabActive: {
    color: '#ffffff',
    background: '#0f172a',
  },
  editorHint: {
    background: '#0f172a',
    padding: '8px 16px',
    fontSize: '12px',
    color: '#94a3b8',
    borderBottom: '1px solid #1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  editorHintIcon: {
    color: '#60a5fa',
    fontSize: '14px',
  },
  editor: {
    flex: 1,
    overflow: 'hidden',
    minHeight: '200px',
    height: 0,
  },
  // Resizable divider
  resizeDivider: {
    width: '8px',
    background: '#e2e8f0',
    cursor: 'col-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease',
    position: 'relative',
    flexShrink: 0,
    '&:hover': {
      background: '#3b82f6',
    },
    '&::before': {
      content: '""',
      width: '4px',
      height: '60px',
      background: '#94a3b8',
      borderRadius: '2px',
    },
    '&:hover::before': {
      background: '#ffffff',
    },
    [DARK]: {
      background: S.line,
      '&:hover': { background: S.blue },
      '&::before': { background: S.muted },
      '&:hover::before': { background: S.onFg },
    },
  },
  resizeDividerActive: {
    background: '#3b82f6',
    '&::before': {
      background: '#ffffff',
    },
    [DARK]: { background: S.blue, '&::before': { background: S.onFg } },
  },
  // Preview container with expand support
  previewContainer: {
    background: A.surface,
    borderLeft: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    minWidth: '300px',
    transition: 'flex 0.3s ease',
    [DARK]: { borderLeftColor: S.line },
  },
  previewContainerExpanded: {
    flex: '1 1 auto',
  },
  previewHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [DARK]: { borderBottomColor: S.line },
  },
  previewTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#0f172a',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    [DARK]: { color: S.fg },
  },
  previewTitleIcon: {
    color: '#64748b',
    [DARK]: { color: S.muted },
  },
  previewControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  expandButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    background: A.surface,
    color: '#64748b',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '& .material-icons': {
      fontSize: '18px',
    },
    '&:hover': {
      background: '#f8fafc',
      color: '#0f172a',
      borderColor: '#cbd5e1',
    },
    [DARK]: {
      color: S.muted,
      borderColor: S.line,
      '&:hover': { background: A.rowHover, color: S.fg, borderColor: S.fieldLine },
    },
  },
  expandButtonActive: {
    background: '#3b82f6',
    color: '#ffffff',
    borderColor: '#3b82f6',
    '&:hover': {
      background: '#2563eb',
      color: '#ffffff',
      borderColor: '#2563eb',
    },
    [DARK]: {
      background: S.blue,
      color: S.onFg,
      borderColor: S.blue,
      '&:hover': { background: S.blue, color: S.onFg, borderColor: S.blue },
    },
  },
  deviceToggle: {
    display: 'flex',
    gap: '4px',
    background: '#f1f5f9',
    padding: '4px',
    borderRadius: '8px',
    [DARK]: { background: S.chromeHover },
  },
  deviceButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '6px',
    background: 'transparent',
    color: '#64748b',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '& .material-icons': {
      fontSize: '18px',
    },
    '&:hover': {
      background: '#e2e8f0',
      color: '#334155',
    },
    [DARK]: { color: S.muted, '&:hover': { background: S.line, color: S.fg } },
  },
  deviceButtonActive: {
    background: A.surface,
    color: '#0f172a',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    '&:hover': {
      background: A.surface,
      color: '#0f172a',
    },
    [DARK]: { color: S.fg, '&:hover': { background: A.surface, color: S.fg } },
  },
  previewContent: {
    flex: 1,
    overflow: 'auto',
    background: '#f8fafc',
    display: 'flex',
    flexDirection: 'column',
    [DARK]: { background: S.bg },
  },
  // The widget's own rendering: stays white in both themes, like the page canvas.
  previewFrame: {
    background: '#ffffff',
    boxShadow: '0 0 0 1px #e2e8f0',
    flex: 1,
    overflow: 'hidden',
    [DARK]: { boxShadow: `0 0 0 1px ${S.line}` },
  },
  previewDeviceLabel: {
    textAlign: 'center',
    padding: '8px',
    fontSize: '12px',
    color: '#64748b',
    background: '#f1f5f9',
    borderTop: '1px solid #e2e8f0',
    [DARK]: { color: S.muted, background: S.chrome, borderTopColor: S.line },
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#64748b',
    [DARK]: { color: S.muted },
  },
  error: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#b91c1c',
    padding: '40px',
    textAlign: 'center',
    [DARK]: { color: S.red },
  },
  saveStatus: {
    fontSize: '12px',
    color: '#64748b',
    [DARK]: { color: S.muted },
  },
  dirty: {
    color: '#f59e0b',
    [DARK]: { color: S.amber },
  },
  // Resize overlay to capture mouse events during drag
  resizeOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    cursor: 'col-resize',
  },
  // Props JSON import/export toolbar + modal
  propsToolbar: {
    display: 'flex',
    gap: '8px',
    padding: '12px 16px',
    borderBottom: '1px solid #e2e8f0',
    flexShrink: 0,
    [DARK]: { borderBottomColor: S.line },
  },
  // Import/Export JSON share the toolbar's width.
  propsToolbarBtn: { flex: 1 },
  jsonModal: {
    background: A.surface,
    border: '2px solid hsl(0, 0%, 95%)',
    borderRadius: '3px',
    padding: '24px',
    width: '620px',
    maxWidth: '92vw',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    [DARK]: { borderColor: S.line },
  },
  jsonModalTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: 'hsl(0, 0%, 20%)',
    marginBottom: '6px',
    [DARK]: { color: S.fg },
  },
  jsonHint: {
    fontSize: '12px',
    color: '#64748b',
    marginBottom: '12px',
    lineHeight: 1.5,
    paddingRight: '20px',
    [DARK]: { color: S.muted },
  },
  jsonTextarea: {
    width: '100%',
    height: '300px',
    fontFamily: 'monospace',
    fontSize: '12px',
    lineHeight: 1.5,
    padding: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    resize: 'vertical',
    boxSizing: 'border-box',
    '&:focus': { outline: 'none', borderColor: '#3b82f6' },
    [DARK]: {
      background: S.field,
      color: S.fg,
      borderColor: S.fieldLine,
      '&:focus': { borderColor: S.blue },
    },
  },
  jsonError: {
    marginTop: '10px',
    padding: '8px 12px',
    background: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    color: '#b91c1c',
    fontSize: '12px',
    whiteSpace: 'pre-wrap',
    [DARK]: { background: TONES.red.bg, borderColor: TONES.red.bg, color: TONES.red.fg },
  },
  jsonModalActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    marginTop: '16px',
  },
  // Publish Modal Styles - matching Genesis design system
  publishModalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'hsla(0, 0%, 0%, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 12,
    [DARK]: { background: S.scrim },
  },
  publishModal: {
    background: A.surface,
    border: '2px solid hsl(0, 0%, 95%)',
    borderRadius: '3px',
    padding: '25px',
    width: '430px',
    minHeight: '230px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    [DARK]: { borderColor: S.line },
  },
  publishModalClose: {
    position: 'absolute',
    right: '12px',
    top: '11px',
    fontSize: '17px',
    color: 'hsl(0, 0%, 60%)',
    cursor: 'pointer',
    '&:hover': {
      color: 'hsl(0, 0%, 40%)',
    },
    [DARK]: { color: S.muted, '&:hover': { color: S.fg } },
  },
  publishModalIcon: {
    width: '50px',
    height: '50px',
    marginBottom: '15px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  publishModalIconConfirm: {
    background: 'hsl(160, 50%, 50%)',
    color: 'hsl(0, 0%, 100%)',
  },
  publishModalIconPublishing: {
    background: 'hsl(45, 90%, 55%)',
    color: 'hsl(0, 0%, 100%)',
    animation: '$pulse 1.5s ease-in-out infinite',
  },
  publishModalIconSuccess: {
    background: 'hsl(160, 50%, 50%)',
    color: 'hsl(0, 0%, 100%)',
  },
  publishModalTitle: {
    fontSize: '23px',
    fontWeight: 300,
    color: 'hsl(0, 0%, 20%)',
    marginBottom: '10px',
    textAlign: 'center',
    [DARK]: { color: S.fg },
  },
  publishModalMessage: {
    fontSize: '14px',
    fontWeight: 300,
    color: 'hsl(0, 0%, 50%)',
    marginBottom: '20px',
    lineHeight: 1.5,
    textAlign: 'center',
    [DARK]: { color: S.muted },
  },
  publishModalVersion: {
    display: 'block',
    background: 'hsl(0, 0%, 95%)',
    padding: '5px 12px',
    borderRadius: '3px',
    fontSize: '13px',
    fontWeight: 400,
    color: 'hsl(0, 0%, 30%)',
    marginTop: '10px',
    [DARK]: { background: TONES.gray.bg, color: TONES.gray.fg },
  },
  publishModalActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center',
    marginTop: '10px',
  },
  publishingSpinner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '15px',
  },
  spinnerDots: {
    display: 'flex',
    gap: '8px',
  },
  spinnerDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: 'hsl(160, 50%, 50%)',
    animation: '$bounce 1.4s ease-in-out infinite',
    '&:nth-child(1)': {
      animationDelay: '0s',
    },
    '&:nth-child(2)': {
      animationDelay: '0.2s',
    },
    '&:nth-child(3)': {
      animationDelay: '0.4s',
    },
  },
  '@keyframes pulse': {
    '0%, 100%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.1)',
    },
  },
  '@keyframes bounce': {
    '0%, 80%, 100%': {
      transform: 'translateY(0)',
    },
    '40%': {
      transform: 'translateY(-10px)',
    },
  },
});

interface PropsSchemaItem {
  key: string;
  label: string;
  type: string;
  defaultValue?: any;
  options?: { label: string; value: string }[];
  children?: PropsSchemaItem[];
}

interface WidgetVersion {
  versionId: string;
  html: string;
  css: string;
  js: string;
  propsSchema: PropsSchemaItem[];
  defaultProps: Record<string, any>;
  changelog?: string;
  createdAt: string;
  scoper?: number | null;
}

interface CustomWidget {
  _id: string;
  name: string;
  title: string;
  description?: string;
  category?: string;
  publishedVersion?: string;
  isActive: boolean;
  widgetId: string;
  versions: WidgetVersion[];
  lockedBy?: {
    userId: string;
    userName: string;
    lockedAt: string;
    socketId: string;
  };
}

type EditorTab = 'html' | 'css' | 'js';
type SidebarTab = 'props' | 'settings';
type PreviewDevice = 'mobile' | 'tablet' | 'desktop';
type PublishStep = 'confirm' | 'publishing' | 'success' | null;

const DEVICE_WIDTHS: Record<PreviewDevice, number | '100%'> = {
  mobile: 390,
  tablet: 900,
  desktop: '100%',
};

const WidgetBuilder: React.FC = () => {
  const c = useStyles();
  const { id } = useParams<{ id: string }>();

  // State
  const [activeEditorTab, setActiveEditorTab] = useState<EditorTab>('html');
  const [activeSidebarTab, setActiveSidebarTab] = useState<SidebarTab>('props');
  const [currentVersionId, setCurrentVersionId] = useState<string>('draft');
  // A version picked while the draft has unsaved changes: switching would reload the code over them.
  const [pendingVersionId, setPendingVersionId] = useState<string | null>(null);
  const [html, setHtml] = useState('');
  const [css, setCss] = useState('');
  const [js, setJs] = useState('');
  const [propsSchema, setPropsSchema] = useState<PropsSchemaItem[]>([]);
  const [defaultProps, setDefaultProps] = useState<Record<string, any>>({});
  const [isDirty, setIsDirty] = useState(false);
  // The builder has the whole window. Leaving with unsaved changes asks first: the back
  // link and the browser's Back/Forward (blocked here, then retried after "Leave"),
  // and reloading or closing the tab (beforeunload).
  const history = useHistory();
  const [confirmLeave, setConfirmLeave] = useState(false);
  // Questions asked in the builder's own dialog: the browser's confirm box stops keyboard users and AI assistants.
  const [pendingConfirm, setPendingConfirm] = useState<{ title: string; body: string; confirmLabel: string; danger?: boolean; run: () => unknown } | null>(null);
  const blockedLeaveRef = useRef<{ location: { pathname: string; search?: string; hash?: string }; action: string } | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    const unblock = history.block((location, action) => {
      blockedLeaveRef.current = { location, action };
      setConfirmLeave(true);
      return false;
    });
    unblockRef.current = unblock;
    return () => {
      window.removeEventListener('beforeunload', warn);
      unblock();
      unblockRef.current = null;
    };
  }, [isDirty, history]);
  const leaveAnyway = () => {
    if (unblockRef.current) unblockRef.current();
    unblockRef.current = null;
    setIsDirty(false);
    const blocked = blockedLeaveRef.current;
    blockedLeaveRef.current = null;
    if (blocked && blocked.action === 'REPLACE') history.replace(blocked.location);
    else history.push(blocked ? blocked.location : '/admin/widgets');
  };
  // 2 = this draft/version is scoped by the v2 scoper (code pasted with the paste tools,
  // or switched in Settings). Unset = the original scoper, as all earlier versions.
  const [scoper, setScoper] = useState<number | undefined>(undefined);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  // DISABLED: Socket connection removed - lock logic disabled, to be revisited
  // const [socket, setSocket] = useState<Socket | null>(null);
  const [lockedByOther, setLockedByOther] = useState<string | null>(null);
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop');
  const [publishStep, setPublishStep] = useState<PublishStep>(null);
  const [publishedVersionId, setPublishedVersionId] = useState<string>('');
  const [redeployPages, setRedeployPages] = useState<Array<{ title: string; path: string; pageVersionId: string }> | null>(null);
  const currentUser = useSelector((state: { app?: { user?: Record<string, unknown> | null } }) => state.app && state.app.user);
  const canDeploy = hasPermissionAixcDeploy(currentUser);

  // Props JSON import/export modal
  const [propsJsonModal, setPropsJsonModal] = useState<'import' | 'export' | null>(null);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');

  // Ref to skip the version-loading useEffect when restoring old version content to draft
  const skipContentReloadRef = useRef(false);

  // Panel collapse/resize state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [editorCollapsed, setEditorCollapsed] = useState(false);
  const [editorWidth, setEditorWidth] = useState<number | null>(null); // null = auto flex
  const [sidebarWidth, setSidebarWidth] = useState(300); // Default sidebar width
  const [isResizing, setIsResizing] = useState(false);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Handle resize drag for editor
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  }, []);

  // Handle resize drag for sidebar
  const handleSidebarResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizingSidebar(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      if (!bodyRef.current) return;
      
      const bodyRect = bodyRef.current.getBoundingClientRect();
      const currentSidebarWidth = sidebarCollapsed ? 0 : sidebarWidth;
      const minEditorWidth = 200;
      const minPreviewWidth = 300;
      const dividerWidth = 8;
      const sidebarDividerWidth = sidebarCollapsed ? 0 : 8;
      
      // Calculate maximum editor width (total width minus sidebar, dividers, and min preview)
      const maxEditorWidth = bodyRect.width - currentSidebarWidth - sidebarDividerWidth - dividerWidth - minPreviewWidth;
      
      // Calculate editor width based on mouse position relative to body start
      const mouseX = e.clientX - bodyRect.left - currentSidebarWidth - sidebarDividerWidth;
      
      // Clamp the editor width between min and max
      const newEditorWidth = Math.max(minEditorWidth, Math.min(mouseX, maxEditorWidth));
      setEditorWidth(newEditorWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    // Also stop resizing if mouse leaves the window
    const handleMouseLeave = (e: MouseEvent) => {
      // Only stop if mouse actually left the document
      if (e.relatedTarget === null) {
        setIsResizing(false);
      }
    };

    // Use capture phase for more reliable event handling
    document.addEventListener('mousemove', handleMouseMove, { capture: true });
    document.addEventListener('mouseup', handleMouseUp, { capture: true });
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove, { capture: true });
      document.removeEventListener('mouseup', handleMouseUp, { capture: true });
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isResizing, sidebarCollapsed, sidebarWidth]);

  // Handle sidebar resize drag
  useEffect(() => {
    if (!isResizingSidebar) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      if (!bodyRef.current) return;
      
      const bodyRect = bodyRef.current.getBoundingClientRect();
      const minSidebarWidth = 200;
      const maxSidebarWidth = 500;
      
      // Calculate sidebar width based on mouse position relative to body start
      const mouseX = e.clientX - bodyRect.left;
      
      // Clamp the sidebar width between min and max
      const newSidebarWidth = Math.max(minSidebarWidth, Math.min(mouseX, maxSidebarWidth));
      setSidebarWidth(newSidebarWidth);
    };

    const handleMouseUp = () => {
      setIsResizingSidebar(false);
    };

    // Also stop resizing if mouse leaves the window
    const handleMouseLeave = (e: MouseEvent) => {
      // Only stop if mouse actually left the document
      if (e.relatedTarget === null) {
        setIsResizingSidebar(false);
      }
    };

    // Use capture phase for more reliable event handling
    document.addEventListener('mousemove', handleMouseMove, { capture: true });
    document.addEventListener('mouseup', handleMouseUp, { capture: true });
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove, { capture: true });
      document.removeEventListener('mouseup', handleMouseUp, { capture: true });
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isResizingSidebar]);

  // Toggle fullscreen preview (collapse both sidebar and editor)
  const toggleFullscreenPreview = useCallback(() => {
    if (editorCollapsed && sidebarCollapsed) {
      // Restore both
      setEditorCollapsed(false);
      setSidebarCollapsed(false);
    } else {
      // Collapse both for fullscreen preview
      setEditorCollapsed(true);
      setSidebarCollapsed(true);
    }
  }, [editorCollapsed, sidebarCollapsed]);

  const isFullscreenPreview = editorCollapsed && sidebarCollapsed;

  // Queries & Mutations
  const { data, loading, error, refetch } = useQuery(GET_CUSTOM_WIDGET, {
    variables: { id },
    fetchPolicy: 'network-only',
  });

  const [saveVersion, { loading: saving }] = useMutation(SAVE_WIDGET_VERSION);
  const [publishVersion, { loading: publishing }] = useMutation(PUBLISH_WIDGET_VERSION);
  // DISABLED: Lock mutations - lock logic disabled, to be revisited
  // const [lockWidget] = useMutation(LOCK_WIDGET);
  // const [unlockWidget] = useMutation(UNLOCK_WIDGET);

  const widget: CustomWidget | null = data?.customWidget || null;

  // DISABLED: Socket connection removed - lock logic disabled, to be revisited
  // useEffect(() => {
  //   const newSocket = io(getAppURL());
  //   setSocket(newSocket);
  //
  //   return () => {
  //     newSocket.disconnect();
  //   };
  // }, []);

  // Track editing status (informational only - lock logic disabled)
  // Just check if widget has an existing lockedBy field from the database
  useEffect(() => {
    if (!widget) return;

    // Check if widget is already being edited by someone else (from DB)
    if (widget.lockedBy && widget.lockedBy.userName) {
      setLockedByOther(widget.lockedBy.userName);
    } else {
      setLockedByOther(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget?.lockedBy?.userName]);

  // Load version data
  useEffect(() => {
    if (!widget) return;

    // When restoring an old version to draft, the editor already holds the
    // desired content. Skip the reload so we don't overwrite it.
    if (skipContentReloadRef.current) {
      skipContentReloadRef.current = false;
      return;
    }

    // Find the version to load
    let version: WidgetVersion | undefined;
    if (currentVersionId === 'draft') {
      // First try to find a saved draft entry
      version = widget.versions.find((v) => v.versionId === 'draft');
      // Fall back to latest numbered version for backward compatibility
      // (existing widgets may not have a draft entry yet)
      if (!version) {
        const numberedVersions = widget.versions.filter((v) => v.versionId !== 'draft');
        version = numberedVersions[numberedVersions.length - 1];
      }
    } else {
      version = widget.versions.find((v) => v.versionId === currentVersionId);
    }

    if (version) {
      setHtml(version.html || '');
      setCss(version.css || '');
      setJs(version.js || '');
      setPropsSchema(enrichPropsSchemaItems(version.propsSchema || []));
      setDefaultProps(version.defaultProps || {});
      setScoper(version.scoper === 2 ? 2 : undefined);
    } else {
      // Empty state for new widget
      setHtml('');
      setCss('');
      setJs('');
      setPropsSchema([]);
      setDefaultProps({});
      // Brand-new widgets start on the v2 scoper; nothing depends on the old behavior yet.
      setScoper(2);
    }
    setIsDirty(false);
  }, [widget, currentVersionId]);

  // Generate next version number using proper semantic versioning sort
  // Excludes the "draft" pseudo-version from calculation
  const nextVersionNumber = useMemo(() => {
    if (!widget) return '1.0.0';
    const numberedVersions = widget.versions.filter((v) => v.versionId !== 'draft');
    if (numberedVersions.length === 0) {
      return '1.0.0';
    }
    // Sort using semantic versioning (numeric comparison, not lexicographic)
    const versions = numberedVersions.map((v) => v.versionId).sort((a, b) => {
      const aParts = a.split('.').map(Number);
      const bParts = b.split('.').map(Number);
      for (let i = 0; i < 3; i++) {
        if ((aParts[i] || 0) !== (bParts[i] || 0)) {
          return (aParts[i] || 0) - (bParts[i] || 0);
        }
      }
      return 0;
    });
    const latest = versions[versions.length - 1];
    const parts = latest.split('.').map(Number);
    parts[2] = (parts[2] || 0) + 1;
    return parts.join('.');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget?.versions]);

  // Handle code changes
  const handleCodeChange = useCallback((type: EditorTab, value: string) => {
    setIsDirty(true);
    switch (type) {
      case 'html':
        setHtml(value);
        break;
      case 'css':
        setCss(value);
        break;
      case 'js':
        setJs(value);
        break;
    }
  }, []);

  // Handle AI-generated code application
  const handleApplyAiCode = useCallback((type: 'html' | 'css' | 'js', code: string) => {
    setIsDirty(true);
    switch (type) {
      case 'html':
        setHtml(code);
        setActiveEditorTab('html');
        break;
      case 'css':
        setCss(code);
        setActiveEditorTab('css');
        break;
      case 'js':
        setJs(code);
        setActiveEditorTab('js');
        break;
    }
  }, []);

  // Build default props from schema (including nested structures)
  const buildDefaultProps = useCallback((schema: PropsSchemaItem[], existingProps: Record<string, any> = {}): Record<string, any> => {
    const newDefaultProps: Record<string, any> = {};
    schema.forEach((item) => {
      if (item.type === 'ARRAY') {
        // For arrays, use the defaultValue which contains the default items
        newDefaultProps[item.key] = item.defaultValue ?? existingProps[item.key] ?? [];
      } else if (item.type === 'OBJECT' && item.children && item.children.length > 0) {
        // For objects, recursively build defaults from children
        const existingObj = existingProps[item.key] || {};
        newDefaultProps[item.key] = buildDefaultProps(item.children, existingObj);
      } else {
        newDefaultProps[item.key] = item.defaultValue ?? existingProps[item.key] ?? '';
      }
    });
    return newDefaultProps;
  }, []);

  // Handle props schema changes
  const handlePropsSchemaChange = useCallback((newSchema: PropsSchemaItem[]) => {
    setIsDirty(true);
    setPropsSchema(newSchema);
    
    // Update default props to match schema
    const newDefaultProps = buildDefaultProps(newSchema, defaultProps);
    setDefaultProps(newDefaultProps);
  }, [defaultProps, buildDefaultProps]);

  // Open the JSON import modal (empty textarea)
  const openImportPropsJson = useCallback(() => {
    setJsonText('');
    setJsonError('');
    setPropsJsonModal('import');
  }, []);

  // Open the JSON export modal, prefilled with the current props
  const openExportPropsJson = useCallback(() => {
    setJsonText(exportPropsJson(propsSchema, defaultProps));
    setJsonError('');
    setPropsJsonModal('export');
  }, [propsSchema, defaultProps]);

  // Parse the pasted JSON and replace the current props schema + defaults
  const handleImportPropsJson = useCallback(() => {
    let parsed;
    try {
      parsed = parsePropsJson(jsonText);
    } catch (err: any) {
      setJsonError(err?.message || 'Could not import JSON');
      return;
    }
    const imported = parsed;
    const apply = () => {
      setPropsSchema(imported.schema);
      setDefaultProps(imported.defaultProps);
      setIsDirty(true);
      setPropsJsonModal(null);
      setJsonText('');
      setJsonError('');
      setSaveMessage(`Imported ${imported.schema.length} prop${imported.schema.length === 1 ? '' : 's'}`);
      setTimeout(() => setSaveMessage(''), 3000);
    };
    if (propsSchema.length > 0) {
      setPendingConfirm({
        title: 'Replace the props?',
        body: `Replace the current ${propsSchema.length} prop${propsSchema.length === 1 ? '' : 's'} with the ${imported.schema.length} imported one${imported.schema.length === 1 ? '' : 's'}?`,
        confirmLabel: 'Replace props',
        danger: true,
        run: apply
      });
      return;
    }
    apply();
  }, [jsonText, propsSchema.length]);

  // Copy the exported JSON to the clipboard
  const handleCopyPropsJson = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      setSaveMessage('Copied to clipboard');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch {
      // Clipboard may be blocked; the user can still select and copy manually.
    }
  }, [jsonText]);

  // Helper to strip __typename from objects (Apollo adds this when fetching)
  const stripTypename = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(stripTypename);
    }
    if (obj && typeof obj === 'object') {
      const cleaned: any = {};
      for (const key of Object.keys(obj)) {
        if (key !== '__typename') {
          cleaned[key] = stripTypename(obj[key]);
        }
      }
      return cleaned;
    }
    return obj;
  };

  // Helper to increment version string
  const incrementVersion = (version: string): string => {
    const parts = version.split('.').map(Number);
    parts[2] = (parts[2] || 0) + 1;
    return parts.join('.');
  };

  // Save draft - always writes to the single "draft" pseudo-version
  const handleSave = async (skipConfirm = false): Promise<string | null> => {
    if (!widget) return null;
    
    // Warn if someone else is editing: ask, then save again without asking.
    if (lockedByOther && !skipConfirm) {
      setPendingConfirm({
        title: `${lockedByOther} is editing this widget`,
        body: `Saving will override their changes. Do you want to continue?`,
        confirmLabel: 'Save anyway',
        danger: true,
        run: () => handleSave(true)
      });
      return null;
    }

    const cleanedPropsSchema = stripTypename(propsSchema);
    const cleanedDefaultProps = stripTypename(defaultProps);

    try {
      await saveVersion({
        variables: {
          id: widget._id,
          version: {
            versionId: 'draft',
            html,
            css,
            js,
            propsSchema: cleanedPropsSchema,
            defaultProps: cleanedDefaultProps,
            changelog: '',
            scoper: scoper === 2 ? 2 : null,
          },
        },
      });
      setIsDirty(false);
      setSaveMessage('Draft saved');
      setTimeout(() => setSaveMessage(''), 3000);
      await refetch();
      return 'draft';
    } catch (err: any) {
      console.error('Failed to save draft:', err);
      setSaveMessage('Failed to save');
      return null;
    }
  };

  // Save as a new numbered version (used during publish)
  const saveAsNewVersion = async (): Promise<string | null> => {
    if (!widget) return null;

    const cleanedPropsSchema = stripTypename(propsSchema);
    const cleanedDefaultProps = stripTypename(defaultProps);

    let versionToSave = nextVersionNumber;
    const maxRetries = 5;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        await saveVersion({
          variables: {
            id: widget._id,
            version: {
              versionId: versionToSave,
              html,
              css,
              js,
              propsSchema: cleanedPropsSchema,
              defaultProps: cleanedDefaultProps,
              changelog: '',
              scoper: scoper === 2 ? 2 : null,
            },
          },
        });
        await refetch();
        return versionToSave;
      } catch (err: any) {
        const errorMessage = err?.message || err?.graphQLErrors?.[0]?.message || '';
        const isPublishedVersionError = errorMessage.includes('Cannot modify a published version');
        
        if (isPublishedVersionError && attempt < maxRetries - 1) {
          versionToSave = incrementVersion(versionToSave);
          continue;
        }
        
        console.error('Failed to save version:', err);
        return null;
      }
    }
    
    return null;
  };

  // Show publish confirmation modal
  const handlePublishClick = () => {
    setPublishStep('confirm');
  };

  // Actually perform the publish: creates a new numbered version, then publishes it
  const handleConfirmPublish = async () => {
    if (!widget) return;

    setPublishStep('publishing');

    // Sync draft first so the draft entry matches the current editor state.
    // This prevents the draft from being stale when refetch triggers the
    // version-loading useEffect after saveAsNewVersion.
    await handleSave(true);

    // Save current editor state as a new numbered version
    const savedVersion = await saveAsNewVersion();
    if (!savedVersion) {
      setSaveMessage('Failed to save version for publishing');
      setPublishStep(null);
      return;
    }

    try {
      const startTime = Date.now();
      
      await publishVersion({
        variables: {
          id: widget._id,
          versionId: savedVersion,
        },
      });
      
      // Ensure minimum loading time for better UX
      const elapsed = Date.now() - startTime;
      const minDelay = 1500;
      if (elapsed < minDelay) {
        await new Promise(resolve => setTimeout(resolve, minDelay - elapsed));
      }
      
      setPublishedVersionId(savedVersion);
      setPublishStep('success');
      setIsDirty(false);
      refetch();
    } catch (err) {
      console.error('Failed to publish:', err);
      setSaveMessage('Failed to publish');
      setPublishStep(null);
    }
  };

  // Close publish modal
  const handleClosePublishModal = () => {
    if (publishStep !== 'publishing') {
      setPublishStep(null);
      setPublishedVersionId('');
    }
  };

  // Restore old version to draft (copy old version content to draft for editing)
  const handleRestoreToDraft = () => {
    const version = currentVersionId;
    setPendingConfirm({
      title: `Restore v${version} to the draft?`,
      body: `This copies version ${version}’s content into the draft for editing. Save the draft to keep it.`,
      confirmLabel: 'Restore to draft',
      run: () => {
        // Skip the useEffect content reload so the old version's content
        // (already in html/css/js state) is preserved when we switch to draft.
        skipContentReloadRef.current = true;
        setCurrentVersionId('draft');
        setIsDirty(true);
        setSaveMessage(`Restored from v${version}`);
        setTimeout(() => setSaveMessage(''), 3000);
      }
    });
  };

  if (loading) {
    return (
      <div className={c.container}>
        <div className={c.loading}>Loading widget...</div>
      </div>
    );
  }

  if (error || !widget) {
    return (
      <div className={c.container}>
        <div className={c.error}>
          <h3>Widget not found</h3>
          <p>{error?.message || 'The widget you are looking for does not exist.'}</p>
          <Link to="/admin/widgets">
            {/* eslint-disable-next-line @typescript-eslint/no-empty-function */}
            <Button onClick={() => {}}>Back to Widgets</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Only allow editing on draft - older versions are read-only for safety
  const isViewingOldVersion = currentVersionId !== 'draft';
  const isReadOnly = isViewingOldVersion;

  return (
    <div className={c.container}>
      {pendingVersionId && (
        <ConfirmDialog
          danger
          title={`Show ${pendingVersionId === 'draft' ? 'the draft' : `v${pendingVersionId}`} and lose your changes?`}
          confirmLabel="Discard changes"
          cancelLabel="Keep editing"
          onConfirm={() => { setCurrentVersionId(pendingVersionId); setPendingVersionId(null); }}
          onClose={() => setPendingVersionId(null)}
        >
          <p>Your unsaved changes to the draft will be lost. Save the draft first to keep them.</p>
        </ConfirmDialog>
      )}
      {pendingConfirm && (
        <ConfirmDialog
          danger={pendingConfirm.danger}
          title={pendingConfirm.title}
          confirmLabel={pendingConfirm.confirmLabel}
          onConfirm={pendingConfirm.run}
          onClose={() => setPendingConfirm(null)}
          testId="builder-confirm"
        >
          <p>{pendingConfirm.body}</p>
        </ConfirmDialog>
      )}
      {confirmLeave && (
        <ConfirmDialog
          danger
          title="Leave without saving?"
          confirmLabel="Leave"
          cancelLabel="Stay"
          onConfirm={leaveAnyway}
          onClose={() => { blockedLeaveRef.current = null; setConfirmLeave(false); }}
        >
          <p>Your unsaved changes to this widget will be lost.</p>
        </ConfirmDialog>
      )}
      {/* Resize overlay - captures mouse events during drag */}
      {(isResizing || isResizingSidebar) && <div className={c.resizeOverlay} />}
      
      {/* Header */}
      <div className={c.header}>
        <div className={c.headerLeft}>
          <Link
            to="/admin/widgets"
            className={c.backLink}
            data-testid="builder-back"
          >
            <i className="material-icons" aria-hidden style={{ fontSize: 18 }}>chevron_left</i>
            Custom widgets
          </Link>
          <div className={c.titleSection}>
            <h1 className={c.title}>{widget.title}</h1>
            <span className={c.widgetId}>{widget.widgetId}</span>
          </div>
        </div>
        <div className={c.headerRight}>
          {lockedByOther && (
            <div className={c.lockWarning}>
              {lockedByOther} is also editing this widget
            </div>
          )}
          {saveMessage && (
            <span className={`${c.saveStatus} ${isDirty ? c.dirty : ''}`}>
              {saveMessage}
            </span>
          )}
          {isDirty && !saveMessage && (
            <span className={`${c.saveStatus} ${c.dirty}`}>Unsaved changes</span>
          )}
          <select
            className={c.versionSelect}
            value={currentVersionId}
            onChange={(e) => { if (isDirty) setPendingVersionId(e.target.value); else setCurrentVersionId(e.target.value); }}
          >
            <option value="draft">Draft</option>
            <optgroup label="Version History (read-only)">
              {widget.versions.filter((v) => v.versionId !== 'draft').slice().reverse().map((v) => (
                <option key={v.versionId} value={v.versionId}>
                  v{v.versionId}
                  {v.versionId === widget.publishedVersion ? ' ✓ published' : ''}
                </option>
              ))}
            </optgroup>
          </select>
          {!isReadOnly && (
            <Button secondary onClick={() => setPasteOpen(true)}>
              Paste from AI
            </Button>
          )}
          <Button
            secondary
            onClick={handleSave}
            disabled={!isDirty || saving || isReadOnly}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          {!isViewingOldVersion && (
            <Button
              onClick={handlePublishClick}
              disabled={publishing}
            >
              Publish
            </Button>
          )}
        </div>
      </div>

      {/* Version Warning Banner */}
      {isViewingOldVersion && (
        <div className={c.versionWarningBanner}>
          <div className={c.versionWarningText}>
            <i className="material-icons" style={{ fontSize: '18px' }}>info</i>
            <span>
              <strong>Viewing v{currentVersionId}</strong> (read-only) — 
              {currentVersionId === widget.publishedVersion 
                ? ' This is the currently published version.'
                : ' This is an older version.'}
            </span>
          </div>
          <div className={c.versionActions}>
            <UIButton size="sm" onClick={() => setCurrentVersionId('draft')}>Back to Draft</UIButton>
            <UIButton size="sm" variant="primary" onClick={handleRestoreToDraft}>Restore to Draft</UIButton>
          </div>
        </div>
      )}

      {/* Body */}
      <div 
        className={c.body} 
        ref={bodyRef}
        style={(isResizing || isResizingSidebar) ? { userSelect: 'none', cursor: 'col-resize' } : undefined}
      >
        {/* Sidebar */}
        <div 
          className={`${c.sidebar} ${sidebarCollapsed ? c.sidebarCollapsed : ''}`}
          style={sidebarCollapsed ? undefined : { width: `${sidebarWidth}px`, transition: isResizingSidebar ? 'none' : 'width 0.3s ease' }}
        >
          <div className={c.tabs}>
            <button
              className={`${c.tab} ${activeSidebarTab === 'props' ? c.tabActive : ''}`}
              onClick={() => setActiveSidebarTab('props')}
            >
              Props
            </button>
            <button
              className={`${c.tab} ${activeSidebarTab === 'settings' ? c.tabActive : ''}`}
              onClick={() => setActiveSidebarTab('settings')}
            >
              Settings
            </button>
          </div>
          {activeSidebarTab === 'props' && (
            <>
              <div className={c.propsToolbar}>
                <UIButton
                  size="sm"
                  icon="content_paste"
                  className={c.propsToolbarBtn}
                  onClick={openImportPropsJson}
                  disabled={isReadOnly}
                  title="Paste JSON to create all props at once"
                >
                  Import JSON
                </UIButton>
                <UIButton
                  size="sm"
                  icon="content_copy"
                  className={c.propsToolbarBtn}
                  onClick={openExportPropsJson}
                  disabled={propsSchema.length === 0}
                  title="Copy the current props as JSON"
                >
                  Export JSON
                </UIButton>
              </div>
              <PropsSchemaEditor
                schema={propsSchema}
                onChange={handlePropsSchemaChange}
                disabled={isReadOnly}
              />
            </>
          )}
          {activeSidebarTab === 'settings' && (
            <div className={c.sidebarSection}>
              <div className={c.sidebarTitle}>Widget Info</div>
              <p className={c.sidebarText} style={{ fontSize: '14px', margin: '0 0 12px 0' }}>
                <strong>Name:</strong> {widget.name}
              </p>
              <p className={c.sidebarText} style={{ fontSize: '14px', margin: '0 0 12px 0' }}>
                <strong>Category:</strong> {widget.category || 'Custom'}
              </p>
              <p className={c.sidebarText} style={{ fontSize: '14px', margin: '0 0 12px 0' }}>
                <strong>Published:</strong> {widget.publishedVersion || 'Not published'}
              </p>
              <p className={c.sidebarText} style={{ fontSize: '14px', margin: '0' }}>
                <strong>Versions:</strong> {widget.versions.filter((v) => v.versionId !== 'draft').length}
              </p>
              <div style={{ marginTop: '20px' }} data-testid="scoping-setting">
                <div className={c.sidebarTitle}>CSS scoping</div>
                <p className={c.sidebarText} style={{ fontSize: '13px', margin: '0 0 10px 0', lineHeight: 1.5 }}>
                  {scoper === 2
                    ? 'Improved: styles stay inside the widget; :root variables, body styles, animations and font imports work as written.'
                    : 'Original: how this widget has always been scoped. The improved scoping keeps styles inside the widget and makes :root variables, body styles, animations and font imports work as written.'}
                </p>
                {!isReadOnly && (
                  <Button
                    secondary
                    onClick={() => { setScoper(scoper === 2 ? undefined : 2); setIsDirty(true); }}
                  >
                    {scoper === 2 ? 'Use original scoping' : 'Use improved scoping'}
                  </Button>
                )}
                {!isReadOnly && (
                  <p className={c.sidebarNote} style={{ fontSize: '12px', margin: '8px 0 0 0', lineHeight: 1.5 }}>
                    Applies to the draft and the versions you publish from it. Check the preview before publishing.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar Resize Divider */}
        {!sidebarCollapsed && (
          <div
            className={`${c.sidebarResizeDivider} ${isResizingSidebar ? c.sidebarResizeDividerActive : ''}`}
            onMouseDown={handleSidebarResizeMouseDown}
            title="Drag to resize sidebar"
          />
        )}

        {/* Main Editor */}
        <div 
          className={`${c.mainContent} ${editorCollapsed ? c.mainContentCollapsed : ''}`}
          style={{
            ...(editorCollapsed 
              ? {} 
              : editorWidth 
                ? { flex: `0 0 ${editorWidth}px`, width: `${editorWidth}px` } 
                : { flex: '0 0 30%' }  // Editor takes 35% by default
            ),
            transition: isResizing ? 'none' : undefined,
          }}
        >
          <div className={c.editorTabs}>
            <button
              className={`${c.editorTab} ${activeEditorTab === 'html' ? c.editorTabActive : ''}`}
              onClick={() => setActiveEditorTab('html')}
            >
              HTML
            </button>
            <button
              className={`${c.editorTab} ${activeEditorTab === 'css' ? c.editorTabActive : ''}`}
              onClick={() => setActiveEditorTab('css')}
            >
              CSS
            </button>
            <button
              className={`${c.editorTab} ${activeEditorTab === 'js' ? c.editorTabActive : ''}`}
              onClick={() => setActiveEditorTab('js')}
            >
              JavaScript
            </button>
          </div>
          <div className={c.editorHint}>
            <span className={c.editorHintIcon}>💡</span>
            {activeEditorTab === 'css' && (
              <span>Use <code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: 4 }}>{'var(--propName)'}</code> to use prop values. CSS is auto-scoped to this widget.</span>
            )}
            {activeEditorTab === 'js' && (
              <span>Use <code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: 4 }}>root</code> to access your widget&apos;s container. Props available via <code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: 4 }}>__WIDGET_PROPS__</code></span>
            )}
            {activeEditorTab === 'html' && (
              <span>Use <code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: 4 }}>{'{{propName}}'}</code> or <code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: 4 }}>{'{{nested.prop}}'}</code> to insert prop values.</span>
            )}
          </div>
          <div className={c.editor}>
            <CodeEditor
              language={activeEditorTab}
              value={activeEditorTab === 'html' ? html : activeEditorTab === 'css' ? css : js}
              onChange={(value) => handleCodeChange(activeEditorTab, value)}
              readOnly={isReadOnly}
            />
          </div>
        </div>

        {/* Resizable Divider */}
        {!editorCollapsed && (
          <div
            className={`${c.resizeDivider} ${isResizing ? c.resizeDividerActive : ''}`}
            onMouseDown={handleResizeMouseDown}
            title="Drag to resize"
          />
        )}

        {/* Preview */}
        <div 
          className={`${c.previewContainer} ${isFullscreenPreview ? c.previewContainerExpanded : ''}`}
          style={{ flex: 1, position: 'relative', transition: isResizing ? 'none' : undefined }}
        >
          <div className={c.previewHeader}>
            <span className={c.previewTitle}>
              <i className={`material-icons ${c.previewTitleIcon}`} style={{ fontSize: '18px' }}>visibility</i>
              Live Preview
            </span>
            <div className={c.previewControls}>
              <button
                className={`${c.expandButton} ${isFullscreenPreview ? c.expandButtonActive : ''}`}
                onClick={toggleFullscreenPreview}
                title={isFullscreenPreview ? 'Exit Fullscreen' : 'Fullscreen Preview'}
              >
                <i className="material-icons">
                  {isFullscreenPreview ? 'fullscreen_exit' : 'fullscreen'}
                </i>
              </button>
              {!editorCollapsed && (
                <button
                  className={c.expandButton}
                  onClick={() => setEditorCollapsed(true)}
                  title="Hide Code Editor"
                >
                  <i className="material-icons">code_off</i>
                </button>
              )}
              <div className={c.deviceToggle}>
                <button
                  className={`${c.deviceButton} ${previewDevice === 'mobile' ? c.deviceButtonActive : ''}`}
                  onClick={() => setPreviewDevice('mobile')}
                  title="Mobile (390px)"
                >
                  <i className="material-icons">smartphone</i>
                </button>
                <button
                  className={`${c.deviceButton} ${previewDevice === 'tablet' ? c.deviceButtonActive : ''}`}
                  onClick={() => setPreviewDevice('tablet')}
                  title="Tablet (900px)"
                >
                  <i className="material-icons">tablet</i>
                </button>
                <button
                  className={`${c.deviceButton} ${previewDevice === 'desktop' ? c.deviceButtonActive : ''}`}
                  onClick={() => setPreviewDevice('desktop')}
                  title="Desktop (100%)"
                >
                  <i className="material-icons">computer</i>
                </button>
              </div>
            </div>
          </div>
          <div className={c.previewContent}>
            <div 
              className={c.previewFrame}
              style={{
                width: DEVICE_WIDTHS[previewDevice] === '100%' ? '100%' : `${DEVICE_WIDTHS[previewDevice]}px`,
                margin: previewDevice !== 'desktop' ? '0 auto' : undefined,
                height: '100%',
                transition: 'width 0.3s ease',
              }}
            >
              <LivePreview 
                html={html} 
                css={css} 
                js={js} 
                props={defaultProps} 
                widgetName={widget.name}
                scoper={scoper}
              />
            </div>
          </div>
          {previewDevice !== 'desktop' && (
            <div className={c.previewDeviceLabel}>
              {previewDevice === 'mobile' ? '390px' : '900px'} width
            </div>
          )}
        </div>
      </div>

      {/* Publish Confirmation Modal */}
      {publishStep && (
        <div className={c.publishModalOverlay} onClick={handleClosePublishModal}>
          <div className={c.publishModal} onClick={(e) => e.stopPropagation()}>
            {publishStep !== 'publishing' && (
              <i
                className={`material-icons ${c.publishModalClose}`}
                onClick={handleClosePublishModal}
              >
                close
              </i>
            )}

            {publishStep === 'confirm' && (
              <>
                <div className={`${c.publishModalIcon} ${c.publishModalIconConfirm}`}>
                  <i className="material-icons">publish</i>
                </div>
                <div className={c.publishModalTitle}>Publish Widget?</div>
                <div className={c.publishModalMessage}>
                  <span className={c.publishModalVersion}>
                    Will be published as v{nextVersionNumber}
                  </span>
                </div>
                <div className={c.publishModalActions}>
                  <UIButton onClick={handleClosePublishModal}>Cancel</UIButton>
                  <UIButton variant="primary" onClick={handleConfirmPublish}>Publish</UIButton>
                </div>
              </>
            )}

            {publishStep === 'publishing' && (
              <>
                <div className={`${c.publishModalIcon} ${c.publishModalIconPublishing}`}>
                  <i className="material-icons">cloud_upload</i>
                </div>
                <div className={c.publishModalTitle}>Publishing...</div>
                <div className={c.publishingSpinner}>
                  <div className={c.spinnerDots}>
                    <div className={c.spinnerDot}></div>
                    <div className={c.spinnerDot}></div>
                    <div className={c.spinnerDot}></div>
                  </div>
                  <div className={c.publishModalMessage} style={{ marginBottom: 0 }}>
                    Making your widget live...
                  </div>
                </div>
              </>
            )}

            {publishStep === 'success' && (
              <>
                <div className={`${c.publishModalIcon} ${c.publishModalIconSuccess}`}>
                  <i className="material-icons">check</i>
                </div>
                <div className={c.publishModalTitle}>Published!</div>
                <div className={c.publishModalMessage}>
                  Version {publishedVersionId} is now the published version.
                </div>
                {widget && (
                  <PublishImpact
                    widgetName={widget.name}
                    versionId={publishedVersionId}
                    canDeploy={canDeploy}
                    aixcOnly={canDeploy && !(currentUser && currentUser.isSuperAdmin)}
                    onRedeploy={(pages) => {
                      setPublishStep(null);
                      setPublishedVersionId('');
                      setRedeployPages(pages);
                    }}
                  />
                )}
                <div className={c.publishModalActions}>
                  <UIButton variant="primary" onClick={handleClosePublishModal}>Done</UIButton>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {pasteOpen && (
        <PasteCodeModal
          widgetName={widget.name}
          props={defaultProps}
          hasCode={Boolean(html.trim() || css.trim() || js.trim())}
          onClose={() => setPasteOpen(false)}
          onKeep={(code) => {
            setHtml(code.html);
            setCss(code.css);
            setJs(code.js);
            setScoper(code.scoper);
            setIsDirty(true);
            setPasteOpen(false);
            setSaveMessage('Pasted code kept. Save the draft to keep it.');
          }}
        />
      )}

      {redeployPages && (
        <RedeployModal
          pages={redeployPages}
          onDone={() => {
            setRedeployPages(null);
            setSaveMessage('Redeploy finished');
          }}
          onCancel={() => setRedeployPages(null)}
        />
      )}

      {/* Props JSON Import/Export Modal */}
      {propsJsonModal && (
        <div className={c.publishModalOverlay} onClick={() => setPropsJsonModal(null)}>
          <div className={c.jsonModal} onClick={(e) => e.stopPropagation()}>
            <i
              className={`material-icons ${c.publishModalClose}`}
              onClick={() => setPropsJsonModal(null)}
            >
              close
            </i>
            <div className={c.jsonModalTitle}>
              {propsJsonModal === 'import' ? 'Import props from JSON' : 'Export props as JSON'}
            </div>
            <div className={c.jsonHint}>
              {propsJsonModal === 'import'
                ? 'Paste a values object (just defaultProps), a full { "propsSchema", "defaultProps" }, or a schema array. Types and default values are detected automatically — this replaces the current props.'
                : 'Copy this and paste it into another widget’s Import to reuse these props.'}
            </div>
            <textarea
              className={c.jsonTextarea}
              value={jsonText}
              onChange={(e) => {
                setJsonText(e.target.value);
                if (jsonError) setJsonError('');
              }}
              placeholder={'{\n  "title": "Hello",\n  "items": [\n    { "label": "One", "href": "#" }\n  ]\n}'}
              spellCheck={false}
              readOnly={propsJsonModal === 'export'}
            />
            {jsonError && <div className={c.jsonError}>{jsonError}</div>}
            <div className={c.jsonModalActions}>
              {propsJsonModal === 'import' ? (
                <>
                  <UIButton onClick={() => setPropsJsonModal(null)}>Cancel</UIButton>
                  <UIButton variant="primary" onClick={handleImportPropsJson}>Import</UIButton>
                </>
              ) : (
                <>
                  <UIButton onClick={() => setPropsJsonModal(null)}>Close</UIButton>
                  <UIButton variant="primary" icon="content_copy" onClick={handleCopyPropsJson}>Copy</UIButton>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI Chat Panel */}
      {!isReadOnly && (
        <AiChatPanel
          existingCode={{ html, css, js }}
          onApplyCode={handleApplyAiCode}
        />
      )}
    </div>
  );
};

export default WidgetBuilder;

